/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	forwardRef,
	Fragment,
	useCallback,
	useLayoutEffect,
	useEffect,
	useMemo,
	useRef,
	useState,
	type KeyboardEvent,
} from 'react';

import { css, jsx, keyframes } from '@compiled/react';
import { bind, bindAll } from 'bind-event-listener';
import { FormattedMessage, useIntl, type MessageDescriptor } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import Form from '@atlaskit/form/Form';
import Heading from '@atlaskit/heading/heading';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import IconTile from '@atlaskit/icon/icon-tile';
import Image from '@atlaskit/image/image';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { useResizingHeight } from '@atlaskit/motion/use-resizing-height';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Popup } from '@atlaskit/popup/popup';
import type { PopupComponentProps, TriggerProps } from '@atlaskit/popup/types';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import TextArea from '@atlaskit/textarea/text-area';
import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { succeedRemixOpenMenuExperience } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';

import type { RemixHiddenOptionKind } from '../../../types';
import { RemixLabsLozenge } from '../remix-labs-lozenge';
import remixIllustrationSvg from './assets/Remix-Illustration.svg';
import { SUB_TYPE_ICON_URLS } from './constants';
import messages from './messages';
import {
	CascadeVerticalDirectionContext,
	getBottomPlacementForCascadeDirection,
	getCascadeDirectionForAnchor,
	getCascadeVerticalDirectionForPlacement,
	RemixCascadingMenuContent,
	type CascadeFlyoutDirection,
} from './remix-cascading-menu';
import {
	getAnimatedSubTypeIconUrls,
	getSubTypeIconComponent,
	hasAnimatedSubTypeIcon,
} from './subtype-icon-resolver';
import type {
	RemixOption,
	RemixOptionKind,
	RemixOptionSelectionParameters,
	RemixSubType,
} from './types';
import { getRemixMenuOptions } from './utils';

export type {
	KnownSubType,
	RemixOptionKind,
	RemixOptionSelectionParameters,
	RemixOption,
} from './types';
export {
	getTypeDisplayName,
	getSubTypeDisplayName,
	getInfographicSubTypes,
	PAGE_REMIX_OPTIONS,
} from './utils';
export { default as remixOverlayMessages } from './messages';

// ── RemixOverlayHeader styles ──────────────────────────────────────────────
const headerOuterStyle = css({
	position: 'relative',
	width: '100%',
	paddingBlockStart: token('space.300'), // 24px top spacing
	paddingBlockEnd: token('space.300'), // 24px bottom spacing before accordion list
	transition: 'padding 200ms ease-in-out',
});

const headerOuterMinimizedStyle = css({
	paddingBlockStart: token('space.200'), // 16px top spacing in minimized state
	paddingBlockEnd: token('space.200'), // 16px bottom spacing in minimized state
});

const labsPositionStyle = css({
	position: 'absolute',
	top: token('space.150'),
	right: token('space.150'),
	pointerEvents: 'none',
});

const cascadingHeaderWrapperStyle = css({
	position: 'relative',
	width: '100%',
});

const headerIconWrapperStyle = css({
	position: 'absolute',
	left: '50%',
	top: token('space.300'), // 24px — aligned with container top padding
	transform: 'translateX(-50%)',
	width: '64px', // full-size icon in non-expanded state
	height: '64px',
	transition:
		'left 200ms ease-in-out, top 200ms ease-in-out, width 200ms ease-in-out, height 200ms ease-in-out, transform 200ms ease-in-out, opacity 100ms ease-out',
});

const headerIconWrapperMinimizedStyle = css({
	left: token('space.200'), // 16px from left
	top: token('space.200'), // 16px from top in minimized state
	transform: 'translateX(0)',
	width: '48px', // reduced icon size in minimized state
	height: '48px',
});

// 92px = space.300 (24px top padding) + 64px icon + 4px gap
const headerHeadingStyle = css({
	position: 'absolute',
	left: '50%',
	top: '92px',
	transform: 'translateX(-50%)',
	transition: 'left 200ms ease-in-out, top 200ms ease-in-out, transform 200ms ease-in-out',
	whiteSpace: 'nowrap',
});

// left: 16px (space.200) + 48px icon + 8px gap = 72px
// top: vertically centered with 48px icon — icon center is at 16px + 24px = 40px,
// heading line-height ~24px, so top = 40px - 12px = 28px
const headerHeadingMinimizedStyle = css({
	left: `calc(${token('space.200')} + 56px)`,
	top: '28px',
	transform: 'translateX(0)',
});

// 120px = 92px heading top + ~28px heading height
const headerSubtitleStyle = css({
	position: 'absolute',
	left: '50%',
	top: '120px',
	transform: 'translateX(-50%)',
	transition:
		'left 200ms ease-in-out, top 200ms ease-in-out, transform 200ms ease-in-out, opacity 60ms ease-in 170ms',
	whiteSpace: 'nowrap',
});

// Hide subtitle in minimized state — fast fade out, delayed fade in handled by base style
const headerSubtitleMinimizedStyle = css({
	opacity: 0,
	pointerEvents: 'none',
	transition:
		'left 200ms ease-in-out, top 200ms ease-in-out, transform 200ms ease-in-out, opacity 30ms ease-out',
});

const subtitleStyle = css({
	color: token('color.text.subtlest'),
});

// 113px = 64px icon + 4px gap + ~28px heading + ~4px gap + ~13px subtitle
const headerSpacerStyle = css({
	height: '113px',
	transition: 'height 200ms ease-in-out',
});

// 48px = minimized icon height (no subtitle in minimized state)
const headerSpacerMinimizedStyle = css({
	height: '48px',
});

// ── RemixSubTypeGrid styles ────────────────────────────────────────────────
const gridStyles = cssMap({
	grid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, 1fr)',
		gap: token('space.100'),
		width: '100%',
		paddingBlockStart: token('space.100'),
	},
	tile: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '60px',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface.overlay'),
		cursor: 'pointer',
		overflow: 'hidden',
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
			borderColor: 'transparent',
		},
		transition: token('motion.button.hovered'),
	},
});

// ── RemixAccordionOption styles ────────────────────────────────────────────
const accordionStyles = cssMap({
	accordionWrapper: {
		width: '100%',
		borderRadius: token('radius.large'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
	accordionHeader: {
		width: '100%',
		height: '48px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		backgroundColor: token('color.background.neutral.subtle'),
		borderWidth: '0',
		borderRadius: token('radius.large'),
		paddingBlock: token('space.100'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.150'),
		cursor: 'pointer',
	},
	expandedContent: {
		paddingInline: token('space.150'),
		paddingBlockEnd: token('space.0'),
	},
	pickForMeWrapper: {
		paddingBlockStart: token('space.075'),
		paddingBlockEnd: token('space.075'),
		paddingInline: token('space.075'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		marginBlockStart: token('space.150'),
	},
});

const accordionCollapsedStyle = css({
	backgroundColor: token('elevation.surface.overlay'),
	transition: 'background-color 200ms ease-out, box-shadow 200ms ease-out',
	borderRadius: token('radius.large'),
	'&:hover': {
		backgroundColor: token('elevation.surface.hovered'),
		boxShadow: token('elevation.shadow.overlay'),
	},
});

const accordionExpandedStyle = css({
	backgroundColor: token('elevation.surface.overlay'),
	borderRadius: token('radius.large'),
});

const revealContentStyle = css({
	overflow: 'hidden',
});

const pickForMeButtonStyle = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		fontWeight: token('font.weight.regular'),
		width: '100%',
		borderRadius: token('radius.small'),
	},
});

// ── RemixOverlay styles ────────────────────────────────────────────────────
const modalContentWrapperStyle = css({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 auto',
	overflow: 'hidden',
	backgroundColor: token('elevation.surface.sunken'),
	borderRadius: 'inherit',
});

// ── RemixPopup styles ──────────────────────────────────────────────────────
const subtleSlideIn = keyframes({
	from: { opacity: 0, transform: 'translateX(-8px)' },
	to: { opacity: 1, transform: 'translateX(0)' },
});

const popupStyles = cssMap({
	popupEnterAnimation: {
		animationName: subtleSlideIn,
		animationDuration: '200ms',
		animationTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
		animationFillMode: 'backwards',
		'@media (prefers-reduced-motion: reduce)': {
			animation: 'none',
		},
	},
	contentWrapper: {
		display: 'flex',
		flexDirection: 'column',
		width: '320px',
		overflow: 'hidden',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.large'),
		// Shadow is provided by ADS Popup's default popupComponent — no need to add one here.
	},
	cascadingContentWrapper: {
		display: 'flex',
		flexDirection: 'column',
		overflow: 'visible',
		backgroundColor: 'transparent',
		// Each cascading-menu layer owns its overlay surface, radius, and shadow. Keeping
		// this wrapper transparent makes the gaps between those independent surfaces visible.
	},
	body: {
		display: 'flex',
		flexDirection: 'column',
		flex: '1 1 auto',
		overflow: 'hidden',
	},
	cascadingBody: {
		display: 'flex',
		flexDirection: 'column',
		overflow: 'visible',
	},
	cascadingHeader: {
		width: '100%',
		textAlign: 'center',
	},
	cascadingHeaderIcon: {
		width: '56px',
		height: '56px',
	},
});

const transparentPopupContainerStyle = css({
	backgroundColor: 'transparent',
	boxShadow: 'none',
});

const CascadingPopupContainer = forwardRef<HTMLDivElement, PopupComponentProps>((props, ref) => {
	const {
		shouldRenderToParent: _shouldRenderToParent,
		shouldFitContainer: _shouldFitContainer,
		appearance: _appearance,
		xcss: _xcss,
		isReferenceHidden: _isReferenceHidden,
		children,
		'data-testid': dataTestId,
		'data-placement': dataPlacement,
		...htmlAttributes
	} = props;
	const verticalDirection = getCascadeVerticalDirectionForPlacement(dataPlacement);

	return (
		<CascadeVerticalDirectionContext.Provider value={verticalDirection}>
			<div
				ref={ref}
				css={transparentPopupContainerStyle}
				data-testid={dataTestId ?? 'remix-cascading-popup-container'}
				data-remix-cascading-popup
				data-toolbar-nested-dropdown-menu
				data-placement={dataPlacement}
				{...htmlAttributes}
			>
				{children}
			</div>
		</CascadeVerticalDirectionContext.Provider>
	);
});
CascadingPopupContainer.displayName = 'CascadingPopupContainer';

const scrollableBodyStyle = css({
	overflowY: 'auto',
	flex: '1 1 auto',
});

const remixStyles = cssMap({
	describeInputBorderWrapper: {
		width: '100%',
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
	describeInputWrapper: {
		width: '100%',
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
		backgroundColor: token('elevation.surface.sunken'),
	},
	compactDescribeInputWrapper: {
		width: '100%',
		paddingBlock: token('space.075'),
		paddingInline: token('space.100'),
		backgroundColor: token('elevation.surface.overlay'),
	},
	describePromptRow: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-end',
		gap: token('space.075'),
		width: '100%',
	},
	describeTextAreaWrapper: {
		flex: '1 1 auto',
		minWidth: '0px',
	},
	modalBodyPadding: {
		paddingInline: token('space.200'),
		paddingBlockStart: token('space.050'),
		paddingBlockEnd: token('space.200'),
	},
	remixOptionsStack: {
		width: '100%',
	},
});

// ── RemixOverlayHeader ─────────────────────────────────────────────────────

function RemixOverlayHeader({
	subtitle,
	isMinimized = false,
	showLabsLozenge = true,
}: {
	subtitle?: MessageDescriptor;
	isMinimized?: boolean;
	showLabsLozenge?: boolean;
}) {
	const { formatMessage } = useIntl();
	const subtitleMessage =
		subtitle ??
		(fg('cc_maui_updated_remix_tooltip_text') ? messages.subtitleUpdated : messages.subtitle);

	// Track minimized state changes to trigger the icon fade-out/fade-in animation.
	// When isMinimized changes, quickly fade out at the start, then fade back in
	// near the end of the 200ms position transition.
	const iconRef = useRef<HTMLDivElement>(null);
	const prevMinimizedRef = useRef(isMinimized);

	useEffect(() => {
		if (prevMinimizedRef.current !== isMinimized) {
			prevMinimizedRef.current = isMinimized;
			const el = iconRef.current;
			if (el) {
				// Gently fade out (uses the 100ms ease-out opacity transition from the base style)
				el.style.opacity = '0';

				// Near the end of the 200ms position transition, fade back in
				const timer = setTimeout(() => {
					el.style.opacity = '1';
				}, 130);
				return () => clearTimeout(timer);
			}
		}
	}, [isMinimized]);

	return (
		<div css={[headerOuterStyle, isMinimized && headerOuterMinimizedStyle]}>
			{showLabsLozenge && (
				<div css={labsPositionStyle} data-testid="remix-overlay-labs-lozenge">
					<RemixLabsLozenge />
				</div>
			)}
			<div
				ref={iconRef}
				css={[headerIconWrapperStyle, isMinimized && headerIconWrapperMinimizedStyle]}
				aria-hidden="true"
			>
				<Image src={remixIllustrationSvg} alt={formatMessage(messages.remixIconAlt)} width="100%" />
			</div>
			<div css={[headerHeadingStyle, isMinimized && headerHeadingMinimizedStyle]}>
				<Heading size={isMinimized ? 'medium' : 'large'}>
					<FormattedMessage {...messages.heading} />
				</Heading>
			</div>
			<div css={[headerSubtitleStyle, isMinimized && headerSubtitleMinimizedStyle, subtitleStyle]}>
				<Text size="small">
					<FormattedMessage {...subtitleMessage} />
				</Text>
			</div>
			<div css={[headerSpacerStyle, isMinimized && headerSpacerMinimizedStyle]} />
		</div>
	);
}

// ── RemixSubTypeGrid ───────────────────────────────────────────────────────

interface RemixSubTypeGridProps {
	subTypes: RemixSubType[];
	onSubTypeSelect: (subTypeValue: string) => void;
}

function RemixSubTypeGrid({ subTypes, onSubTypeSelect }: RemixSubTypeGridProps) {
	const { formatMessage } = useIntl();
	// Tiles opt into keyboard-focus artwork motion by rendering `data-remix-artwork-tile`, which the
	// artwork stylesheets key their `:focus-visible` rules off.
	const isKeyboardArtworkMotionEnabled = isExperimentEnabled('cc_maui_polish_changes_batch_7');

	return (
		<Box xcss={gridStyles.grid}>
			{subTypes.map((subType, motionIndex) => {
				const IconComponent = getSubTypeIconComponent(subType.value);
				const label = formatMessage(subType.labelMessage);

				if (process.env.NODE_ENV === 'development' && !IconComponent) {
					// eslint-disable-next-line no-console
					console.warn(`[RemixSubTypeGrid] Missing icon for sub-type: "${subType.value}"`);
				}

				return (
					<Tooltip
						key={subType.value}
						content={label}
						position="top"
						delay={0}
						ignoreTooltipPointerEvents
					>
						{(tooltipProps) => (
							<Pressable
								{...tooltipProps}
								{...(isKeyboardArtworkMotionEnabled ? { 'data-remix-artwork-tile': true } : {})}
								xcss={gridStyles.tile}
								onClick={() => onSubTypeSelect(subType.value)}
								testId={subType.testId}
								aria-label={label}
							>
								{IconComponent ? <IconComponent alt={label} motionIndex={motionIndex} /> : null}
							</Pressable>
						)}
					</Tooltip>
				);
			})}
		</Box>
	);
}

// ── RemixAccordionOption ───────────────────────────────────────────────────

interface RemixAccordionOptionProps {
	option: RemixOption;
	isExpanded: boolean;
	onToggle: (type: RemixOptionKind) => void;
	onSubTypeSelect: (type: RemixOptionKind, subType: string) => void;
	onPickForMe: (type: RemixOptionKind) => void;
	colorMode: string | undefined;
}

const animationConfig = { duration: () => 450 };

// Tracks preloaded option/theme groups to avoid duplicate work.
const preloadedIconGroups = new Set<string>();

/**
 * Preloads sub-type icon images for a remix option so they display instantly
 * when the accordion expands. Only fetches icons for the active color mode.
 */
function preloadSubTypeIcons(option: RemixOption, colorMode: string | undefined): void {
	const preloadKey = `${option.type}-${colorMode}`;
	if (preloadedIconGroups.has(preloadKey)) {
		return;
	}
	preloadedIconGroups.add(preloadKey);

	const isDark = colorMode === 'dark';
	for (const subType of option.subTypes) {
		const animatedUrls = getAnimatedSubTypeIconUrls(subType.value);
		if (animatedUrls) {
			const img = new window.Image();
			img.src = isDark ? animatedUrls.dark : animatedUrls.light;
			continue;
		}
		if (hasAnimatedSubTypeIcon(subType.value)) {
			continue;
		}
		const urls = SUB_TYPE_ICON_URLS[subType.value as keyof typeof SUB_TYPE_ICON_URLS];
		if (urls) {
			const img = new window.Image();
			img.src = isDark ? urls.dark : urls.light;
		}
	}
}

const renderOptionIcon = (option: RemixOption, alt: string) => {
	return (
		<IconTile
			icon={option.icon}
			label={alt}
			appearance={option.iconTileAppearance ?? 'gray'}
			size="small"
		/>
	);
};

function RemixAccordionOption({
	option,
	isExpanded,
	onToggle,
	onSubTypeSelect,
	onPickForMe,
	colorMode,
}: RemixAccordionOptionProps) {
	const { formatMessage } = useIntl();
	const smoothResizer = useResizingHeight(animationConfig);

	const handleToggle = useCallback(() => {
		onToggle(option.type);
	}, [onToggle, option.type]);

	const handleMouseEnter = useCallback(() => {
		preloadSubTypeIcons(option, colorMode);
	}, [option, colorMode]);

	const handleSubTypeSelect = useCallback(
		(subTypeValue: string) => {
			if (fg('cc-maui-phase-3')) {
				const subTypeDef = option.subTypes.find((st) => st.value === subTypeValue);
				onSubTypeSelect(subTypeDef?.emitType ?? option.type, subTypeValue);
				return;
			}

			onSubTypeSelect(option.type, subTypeValue);
		},
		[onSubTypeSelect, option.type, option.subTypes],
	);

	const handlePickForMe = useCallback(() => {
		onPickForMe(option.type);
	}, [onPickForMe, option.type]);

	const ChevronIcon = isExpanded ? ChevronUpIcon : ChevronDownIcon;
	const optionIconAlt = formatMessage(option.iconAltMessage);

	return (
		<div css={[isExpanded ? accordionExpandedStyle : accordionCollapsedStyle]}>
			<Stack xcss={accordionStyles.accordionWrapper}>
				<Pressable
					xcss={accordionStyles.accordionHeader}
					onClick={handleToggle}
					onMouseEnter={handleMouseEnter}
					testId={option.testId}
					aria-expanded={isExpanded}
					aria-label={formatMessage(option.labelMessage)}
				>
					<Flex alignItems="center" gap="space.100">
						{renderOptionIcon(option, optionIconAlt)}
						<Text weight="medium">{formatMessage(option.labelMessage)}</Text>
					</Flex>
					<ChevronIcon label="" color={token('color.icon')} />
				</Pressable>

				<div ref={smoothResizer.ref} css={revealContentStyle}>
					{isExpanded && (
						<Fragment>
							<Box xcss={accordionStyles.expandedContent}>
								<RemixSubTypeGrid
									subTypes={option.subTypes}
									onSubTypeSelect={handleSubTypeSelect}
								/>
							</Box>
							<Box xcss={accordionStyles.pickForMeWrapper}>
								<div css={pickForMeButtonStyle}>
									<Button
										appearance="subtle"
										spacing="compact"
										onClick={handlePickForMe}
										testId={`${option.testId}-pick-for-me`}
									>
										{formatMessage(messages.pickForMe)}
									</Button>
								</div>
							</Box>
						</Fragment>
					)}
				</div>
			</Stack>
		</div>
	);
}

interface RemixFlatOptionProps {
	option: RemixOption;
	onSelect: (type: RemixOptionKind) => void;
}

const RemixFlatOption = ({ option, onSelect }: RemixFlatOptionProps) => {
	const { formatMessage } = useIntl();

	const handleClick = useCallback(() => {
		onSelect(option.type);
	}, [onSelect, option.type]);
	const optionIconAlt = formatMessage(option.iconAltMessage);

	return (
		<div css={accordionCollapsedStyle}>
			<Stack xcss={accordionStyles.accordionWrapper}>
				<Pressable
					xcss={accordionStyles.accordionHeader}
					onClick={handleClick}
					testId={option.testId}
					aria-label={formatMessage(option.labelMessage)}
				>
					<Flex alignItems="center" gap="space.100">
						{renderOptionIcon(option, optionIconAlt)}
						<Text weight="medium">{formatMessage(option.labelMessage)}</Text>
					</Flex>
				</Pressable>
			</Stack>
		</div>
	);
};

interface RemixOverlayContentProps {
	onOptionSelect?: (params: RemixOptionSelectionParameters) => void;
	/** Custom set of options to display. Defaults to the selection-level Remix options. */
	options?: RemixOption[];
	hiddenOptions?: readonly RemixHiddenOptionKind[];
	/** Custom subtitle message. Defaults to "Create a visual from this content". */
	subtitle?: MessageDescriptor;
	showLabsLozenge?: boolean;
}

function setRef<T>(ref: React.Ref<T> | undefined, value: T | null): void {
	if (typeof ref === 'function') {
		ref(value);
		return;
	}
	if (ref) {
		(ref as React.MutableRefObject<T | null>).current = value;
	}
}

interface RemixOverlayProps extends RemixOverlayContentProps {
	onClose: () => void;
}

function RemixOpenMenuExperienceTracker(): null {
	const experienceTracker = useAIMateExperienceTracker();

	useEffect(() => {
		succeedRemixOpenMenuExperience(experienceTracker);
	}, [experienceTracker]);

	return null;
}

/**
 * Placements supported by the anchored RemixPopup.
 * - 'left-end': contextual object-level entry point (opens to the left).
 * - 'bottom-start': toolbar "Remix" button dropdown (opens downward, aligned to trigger start).
 * - 'bottom-end': toolbar "Remix" button dropdown when the cascade needs left-side room.
 */
export type RemixPopupPlacement = 'left-end' | 'bottom-start' | 'bottom-end' | 'right-start';

export type RemixCascadingMenuHeader = {
	title: MessageDescriptor;
	subtitle?: MessageDescriptor;
};

interface RemixPopupProps extends RemixOverlayContentProps {
	isOpen: boolean;
	onClose: () => void;
	trigger: (triggerProps: TriggerProps) => React.ReactNode;
	placement?: RemixPopupPlacement;
	offset?: [number, number];
	/** When this value changes the popup will reposition itself. */
	repositionKey?: number;
	/** When true, render the lightweight cascading menu variant instead of the original popup overlay. */
	useCascadingMenu?: boolean;
	/** Optional localized title and subtitle rendered above the first cascading-menu column. */
	cascadingMenuHeader?: RemixCascadingMenuHeader;
	/** Close the cascading popup when scrolling occurs outside the popup itself. */
	shouldCloseOnScroll?: boolean;
	/** Keep DOM focus where it is while pressing the menu. Opt in for entry points that are unmounted when the editor blurs. */
	shouldPreserveEditorFocus?: boolean;
}

/**
 * A centered modal overlay that displays remix options.
 *
 * Options with sub-types render as expandable accordions with sub-type tiles.
 * Options without sub-types render as flat clickable rows that directly fire selection.
 * Selecting a sub-type emits `{ type, subType }`.
 * The "Pick for me" button emits only `{ type }`, letting the backend choose.
 * The describe input emits `{ type: 'custom', userPrompt }`.
 */
/**
 * Shared inner content for both RemixOverlay (modal) and RemixPopup (popup).
 * Contains the remix options list, accordion, and describe input.
 */
interface RemixDescribeInputProps {
	value: string;
	onChange: (value: string) => void;
	onSubmit: () => void;
	/** Focus on mount. The modal uses this to deselect the editor's highlighted text. */
	autoFocus?: boolean;
	/** Use the reduced padding required by the cascading menu's category card. */
	isCompact?: boolean;
}

/**
 * RemixDescribeInput — free-text prompt row shared by both Remix menu presentations:
 * the centered modal (RemixOverlayContent) and the inline dropdown's category column.
 */
const stopEditorNativeEvent = (event: globalThis.MouseEvent) => {
	// Block controls and block-menu hosts install native document/editor listeners before
	// React's delegated handlers. Stop at the textarea DOM boundary so focusing the prompt
	// cannot be interpreted as an outside click. Do not preventDefault: native focus and
	// text editing must continue to work.
	event.stopPropagation();
	event.stopImmediatePropagation();
};

const bindEditorNativeEventListeners = (element: HTMLElement) =>
	bindAll(element, [
		{ type: 'mousedown', listener: stopEditorNativeEvent },
		{ type: 'mouseup', listener: stopEditorNativeEvent },
		{ type: 'click', listener: stopEditorNativeEvent },
	]);

/**
 * Suppress the default focus transfer for a press inside the menu. `click` still fires, so option
 * selection works. Text controls are exempt because they must be focusable to be typed into.
 */
const preserveFocusOnPress = (event: globalThis.MouseEvent) => {
	const target = event.target;
	if (
		target instanceof Element &&
		target.closest('textarea, input, select, [contenteditable="true"]') !== null
	) {
		return;
	}
	event.preventDefault();
};

const bindPreserveFocusListener = (element: HTMLElement) =>
	bind(element, { type: 'mousedown', listener: preserveFocusOnPress });

/**
 * Keeps Tab away from `@confluence/highlight-actions`' `document`-level listener, which claims every
 * Tab while its selection toolbar is mounted — as it still is when the picker is opened by keyboard.
 * Modal only: ADS `Popup` needs Tab to reach `window` to close and restore focus on tab-out.
 */
const stopTabAtPickerBoundary = (event: globalThis.KeyboardEvent) => {
	if (event.key === 'Tab') {
		event.stopPropagation();
	}
};

/**
 * Callback ref so the listener re-attaches when the picker is closed and reopened.
 */
function useTabBoundaryRef(): (element: HTMLElement | null) => void {
	const unbindRef = useRef<ReturnType<typeof bind> | null>(null);

	return useCallback((element: HTMLElement | null) => {
		unbindRef.current?.();
		unbindRef.current = element
			? bind(element, { type: 'keydown', listener: stopTabAtPickerBoundary })
			: null;
	}, []);
}

function RemixDescribeInput({
	value,
	onChange,
	onSubmit,
	autoFocus = false,
	isCompact = false,
}: RemixDescribeInputProps) {
	const { formatMessage } = useIntl();
	const describeTextAreaRef = useRef<HTMLTextAreaElement | null>(null);
	const unbindDescribeInputBoundaryRef = useRef<ReturnType<typeof bindAll> | null>(null);

	useEffect(() => {
		if (!autoFocus) {
			return;
		}
		// requestAnimationFrame so focus lands after the modal has fully mounted.
		const raf = requestAnimationFrame(() => {
			describeTextAreaRef.current?.focus();
		});
		return () => cancelAnimationFrame(raf);
	}, [autoFocus]);

	const setDescribeInputBoundaryRef = useCallback(
		(element: HTMLDivElement | null) => {
			unbindDescribeInputBoundaryRef.current?.();
			unbindDescribeInputBoundaryRef.current =
				element && isCompact ? bindEditorNativeEventListeners(element) : null;
		},
		[isCompact],
	);

	const handleSubmit = useCallback(() => {
		if (value?.trim()) {
			onSubmit();
		}
	}, [value, onSubmit]);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				handleSubmit();
			}
		},
		[handleSubmit],
	);

	const placeholder = formatMessage(
		fg('cc_maui_updated_remix_tooltip_text')
			? messages.describePlaceholderUpdated
			: messages.describePlaceholder,
	);

	return (
		<Box xcss={remixStyles.describeInputBorderWrapper}>
			<Box
				ref={isCompact ? setDescribeInputBoundaryRef : undefined}
				xcss={
					isCompact ? remixStyles.compactDescribeInputWrapper : remixStyles.describeInputWrapper
				}
				testId="remix-overlay-describe-input-wrapper"
			>
				<Form onSubmit={handleSubmit}>
					<Flex xcss={remixStyles.describePromptRow}>
						<Box xcss={remixStyles.describeTextAreaWrapper}>
							<TextArea
								ref={describeTextAreaRef}
								appearance="none"
								placeholder={placeholder}
								value={value}
								onChange={(e) => onChange(e.currentTarget.value)}
								onKeyDown={handleKeyDown}
								isCompact
								minimumRows={1}
								maxHeight="200px"
								resize="smart"
								aria-label={placeholder}
								testId="remix-overlay-describe-input"
								name="remix-overlay-describe"
							/>
						</Box>
						<IconButton
							type="submit"
							appearance="primary"
							isDisabled={!value?.trim()}
							icon={ArrowUpIcon}
							label={formatMessage(messages.describeSubmitLabel)}
							testId="remix-overlay-describe-submit"
						/>
					</Flex>
				</Form>
			</Box>
		</Box>
	);
}

export function RemixOverlayContent({
	onOptionSelect,
	options,
	hiddenOptions,
	subtitle,
	showLabsLozenge = true,
}: RemixOverlayContentProps): JSX.Element {
	const { colorMode } = useThemeObserver();
	const [describeValue, setDescribeValue] = useState('');
	const [expandedOption, setExpandedOption] = useState<RemixOptionKind | null>(null);
	const { sendAnalyticsEvent } = useAnalytics();

	const resolvedOptions = useMemo(
		() =>
			getRemixMenuOptions({
				source:
					options === undefined
						? { kind: 'preset', preset: 'selection' }
						: {
								kind: 'custom',
								choices: options,
							},
				excludedRemixTypes: hiddenOptions ? [...hiddenOptions] : undefined,
			}),
		[hiddenOptions, options],
	);

	const handleDescribeSubmit = useCallback(() => {
		onOptionSelect?.({ type: 'custom', userPrompt: describeValue });
	}, [describeValue, onOptionSelect]);

	const handleToggle = useCallback(
		(type: RemixOptionKind) => {
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'remixOverlaySection',
				attributes: {
					sectionType: type,
				},
			});
			setExpandedOption((prev) => (prev === type ? null : type));
		},
		[sendAnalyticsEvent],
	);

	const handleSubTypeSelect = useCallback(
		(type: RemixOptionKind, subType: string) => {
			const trimmedDescribe = describeValue?.trim();
			const userPrompt = trimmedDescribe || undefined;
			onOptionSelect?.({ type, subType, userPrompt });
		},
		[onOptionSelect, describeValue],
	);

	const handlePickForMe = useCallback(
		(type: RemixOptionKind) => {
			onOptionSelect?.({ type });
		},
		[onOptionSelect],
	);

	return (
		<Fragment>
			<RemixOverlayHeader
				subtitle={subtitle}
				isMinimized={expandedOption !== null}
				showLabsLozenge={showLabsLozenge}
			/>
			<div css={scrollableBodyStyle}>
				<Box xcss={remixStyles.modalBodyPadding}>
					<Stack space="space.100" alignInline="stretch" xcss={remixStyles.remixOptionsStack}>
						{resolvedOptions.map((option) =>
							option.subTypes.length > 0 ? (
								<RemixAccordionOption
									key={option.type}
									option={option}
									isExpanded={expandedOption === option.type}
									onToggle={handleToggle}
									onSubTypeSelect={handleSubTypeSelect}
									onPickForMe={handlePickForMe}
									colorMode={colorMode}
								/>
							) : (
								<RemixFlatOption key={option.type} option={option} onSelect={handlePickForMe} />
							),
						)}
					</Stack>
				</Box>
			</div>
			<RemixDescribeInput
				value={describeValue}
				onChange={setDescribeValue}
				onSubmit={handleDescribeSubmit}
				autoFocus
			/>
		</Fragment>
	);
}

/**
 * RemixOverlay — centered ADS Modal presentation
 */
export function RemixOverlay({
	onClose,
	onOptionSelect,
	options,
	hiddenOptions,
	subtitle,
	showLabsLozenge = true,
}: RemixOverlayProps): JSX.Element {
	const setTabBoundaryRef = useTabBoundaryRef();

	return (
		<ModalTransition>
			<Modal onClose={onClose} width={320} testId="remix-overlay" shouldReturnFocus={false}>
				<div ref={setTabBoundaryRef} css={modalContentWrapperStyle}>
					<ModalBody hasInlinePadding={false}>
						<RemixOpenMenuExperienceTracker />
						<RemixOverlayContent
							onOptionSelect={onOptionSelect}
							options={options}
							hiddenOptions={hiddenOptions}
							subtitle={subtitle}
							showLabsLozenge={showLabsLozenge}
						/>
					</ModalBody>
				</div>
			</Modal>
		</ModalTransition>
	);
}

/**
 * Calls the Popup `update` function to reposition the popup whenever `repositionKey` changes.
 */
function PopupRepositionOnKeyChange({
	repositionKey,
	update,
}: {
	repositionKey?: string | number;
	update: () => void;
}) {
	const prevKey = useRef(repositionKey);
	useLayoutEffect(() => {
		if (prevKey.current !== repositionKey) {
			prevKey.current = repositionKey;
			update();
		}
	}, [repositionKey, update]);
	return null;
}

/**
 * RemixPopup — anchored ADS Popup presentation for contextual entry points (e.g. object-level sidebar).
 * No blanket, anchored to a trigger element.
 */
export function RemixPopup({
	isOpen,
	onClose,
	trigger,
	onOptionSelect,
	options,
	hiddenOptions,
	subtitle,
	showLabsLozenge = true,
	placement = 'left-end',
	offset = [8, 16],
	repositionKey,
	useCascadingMenu = false,
	cascadingMenuHeader,
	shouldCloseOnScroll = false,
	shouldPreserveEditorFocus = false,
}: RemixPopupProps): JSX.Element {
	const { formatMessage } = useIntl();
	const [cascadeDirection, setCascadeDirection] = useState<CascadeFlyoutDirection>('right');
	const [hasMeasuredCascadeDirection, setHasMeasuredCascadeDirection] = useState(false);
	const [describeValue, setDescribeValue] = useState('');
	const effectivePlacement: RemixPopupPlacement =
		useCascadingMenu && (placement === 'bottom-start' || placement === 'bottom-end')
			? getBottomPlacementForCascadeDirection(cascadeDirection)
			: placement;
	const popupRepositionKey = useCascadingMenu
		? `${repositionKey ?? ''}:${cascadeDirection}`
		: repositionKey;

	useEffect(() => {
		if (!isOpen) {
			setCascadeDirection('right');
			setHasMeasuredCascadeDirection(false);
			setDescribeValue('');
		}
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen || !useCascadingMenu || !shouldCloseOnScroll) {
			return;
		}

		const handleScroll = (event: Event) => {
			const target = event.target;
			if (target instanceof Element && target.closest('[data-remix-cascading-popup]')) {
				return;
			}
			onClose();
		};

		// Scroll does not bubble, so capture on window to observe page and nested scroll containers.
		return bind(window, {
			type: 'scroll',
			listener: handleScroll,
			options: { capture: true },
		});
	}, [isOpen, onClose, shouldCloseOnScroll, useCascadingMenu]);

	const updateCascadeDirectionFromTrigger = useCallback(
		(triggerElement: HTMLElement | null) => {
			if (!useCascadingMenu || !triggerElement || !isOpen) {
				return;
			}
			const document = getDocument();
			const viewportWidth = window.innerWidth || document?.documentElement.clientWidth || 0;
			const nextDirection = getCascadeDirectionForAnchor(
				triggerElement.getBoundingClientRect(),
				viewportWidth,
			);
			setCascadeDirection((currentDirection) =>
				currentDirection === nextDirection ? currentDirection : nextDirection,
			);
			setHasMeasuredCascadeDirection(true);
		},
		[isOpen, useCascadingMenu],
	);

	const unbindPreserveFocusRef = useRef<ReturnType<typeof bind> | null>(null);
	// Callback ref so the listener re-attaches when the popup is closed and reopened.
	const setPreserveFocusRef = useCallback(
		(element: HTMLDivElement | null) => {
			unbindPreserveFocusRef.current?.();
			unbindPreserveFocusRef.current =
				element && shouldPreserveEditorFocus ? bindPreserveFocusListener(element) : null;
		},
		[shouldPreserveEditorFocus],
	);

	return (
		<Popup
			isOpen={isOpen}
			popupComponent={useCascadingMenu ? CascadingPopupContainer : undefined}
			onClose={onClose}
			placement={effectivePlacement}
			offset={offset}
			label={formatMessage(messages.heading)}
			role="dialog"
			content={({ update }) => (
				<Fragment>
					<RemixOpenMenuExperienceTracker />
					<PopupRepositionOnKeyChange repositionKey={popupRepositionKey} update={update} />
					<Box xcss={popupStyles.popupEnterAnimation}>
						<Box
							ref={setPreserveFocusRef}
							data-editor-remix-inline-dropdown-preserve-focus={
								shouldPreserveEditorFocus || undefined
							}
							xcss={
								useCascadingMenu ? popupStyles.cascadingContentWrapper : popupStyles.contentWrapper
							}
							testId="remix-popup"
						>
							<Box xcss={useCascadingMenu ? popupStyles.cascadingBody : popupStyles.body}>
								{useCascadingMenu ? (
									<RemixCascadingMenuContent
										onOptionSelect={onOptionSelect}
										options={options}
										hiddenOptions={hiddenOptions}
										flyoutDirection={cascadeDirection}
										showFlyout={hasMeasuredCascadeDirection}
										header={
											cascadingMenuHeader ? (
												<div css={cascadingHeaderWrapperStyle}>
													<Stack
														xcss={popupStyles.cascadingHeader}
														space="space.050"
														alignInline="center"
														testId="remix-cascading-menu-header-content"
													>
														<Box xcss={popupStyles.cascadingHeaderIcon} aria-hidden="true">
															<Image src={remixIllustrationSvg} alt="" width="100%" />
														</Box>
														<Heading size="medium" color="color.text">
															{formatMessage(cascadingMenuHeader.title)}
														</Heading>
														{cascadingMenuHeader.subtitle ? (
															<Text color="color.text">
																{formatMessage(cascadingMenuHeader.subtitle)}
															</Text>
														) : null}
													</Stack>
												</div>
											) : (
												<Flex
													alignItems="center"
													justifyContent="space-between"
													testId="remix-cascading-menu-header-content"
												>
													<Text weight="semibold" color="color.text">
														{formatMessage(messages.heading)}
													</Text>
													{showLabsLozenge && <RemixLabsLozenge />}
												</Flex>
											)
										}
										headerHasBottomPadding={Boolean(cascadingMenuHeader)}
										headerCornerElement={
											cascadingMenuHeader && showLabsLozenge ? (
												<div css={labsPositionStyle} data-testid="remix-cascading-labs-lozenge">
													<RemixLabsLozenge />
												</div>
											) : undefined
										}
										footer={
											<RemixDescribeInput
												value={describeValue}
												isCompact
												onChange={setDescribeValue}
												onSubmit={() =>
													onOptionSelect?.({ type: 'custom', userPrompt: describeValue })
												}
											/>
										}
									/>
								) : (
									<RemixOverlayContent
										onOptionSelect={onOptionSelect}
										options={options}
										hiddenOptions={hiddenOptions}
										subtitle={subtitle}
										showLabsLozenge={showLabsLozenge}
									/>
								)}
							</Box>
						</Box>
					</Box>
				</Fragment>
			)}
			trigger={(triggerProps) => {
				if (!useCascadingMenu) {
					return trigger(triggerProps);
				}

				return trigger({
					...triggerProps,
					ref: (triggerElement) => {
						setRef(triggerProps.ref, triggerElement);
						updateCascadeDirectionFromTrigger(triggerElement);
					},
				});
			}}
		/>
	);
}
